import { useState } from "react";
import { useDebouncedValue } from "@tellescope/react-hooks";
import { useTellescopeInfiniteQuery, useTellescopeQuery } from "@tellescope/tanstack-query";

interface UseNewFormsExperienceFormsArgs {
  enabled: boolean;
  selectedFormIds: string[];
}

/**
 * Drives the form allowlist picker: searches forms server-side as the admin types, and separately
 * resolves the already-selected ids so their titles survive a search that no longer returns them.
 */
export const useNewFormsExperienceForms = ({ enabled, selectedFormIds }: UseNewFormsExperienceFormsArgs) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300);

  const trimmedQuery = debouncedSearchQuery.trim();

  const searchResultsQuery = useTellescopeInfiniteQuery({
    model: "forms",
    method: "getSome",
    request: trimmedQuery ? { search: { query: trimmedQuery } } : { filter: {} },
    select: (data) => data.pages.flat(),
    enabled,
  });

  const selectedFormsQuery = useTellescopeQuery({
    model: "forms",
    method: "getByIds",
    request: { ids: selectedFormIds },
    enabled: enabled && selectedFormIds.length > 0,
  });

  const searchResults = searchResultsQuery.data ?? [];
  const selectedForms = selectedFormsQuery.data?.matches ?? [];

  const formsById = new Map([...selectedForms, ...searchResults].map((form) => [form.id, form]));

  return {
    searchQuery,
    setSearchQuery,
    formsById,
    isLoading: searchResultsQuery.isLoading || searchResultsQuery.isFetching,
  };
};
